import { useState } from "react";
import AdminCard from "./AdminCard";

interface Props { onSave: () => void; }

const defaultTeam = [
  { id: 1, name: "James Thorpe", role: "Founder & Executive Chairman", bio: "James founded Thorpe Property Development Ltd in Leeds in June 2005 after a decade identifying the structural undersupply of supported housing across Yorkshire and the North.", visible: true },
  { id: 2, name: "Eleanor Hayes", role: "Chief Executive Officer", bio: "Eleanor joined Thorpe Property Development in 2007 as Head of Acquisitions and was appointed CEO in 2014. With over 25 years in structured real estate finance.", visible: true },
  { id: 3, name: "Daniel Reeves", role: "Head of Investment Structuring", bio: "Daniel joined in 2010 after eight years as a government housing adviser at the Department for Levelling Up, Housing and Communities.", visible: true },
  { id: 4, name: "Margaret Blackwell", role: "Chief Financial Officer", bio: "A Chartered Accountant with 22 years in alternative asset management, Margaret joined in 2016 to lead financial governance ahead of the NASDAQ Dubai listing.", visible: true },
  { id: 5, name: "Charlotte Pemberton", role: "Head of Investor Relations", bio: "Charlotte joined in 2019 bringing 12 years of alternative investment distribution experience across private equity and real assets.", visible: true },
  { id: 6, name: "Edward Ashford", role: "Property Acquisition Director", bio: "Edward leads property sourcing, due diligence, and acquisition across England and Wales. Joining in 2012 after a career in residential and commercial development.", visible: true },
];

export default function AdminTeam({ onSave }: Props) {
  const [team, setTeam] = useState(defaultTeam);
  const [sectionTitle, setSectionTitle] = useState("Meet Our People");
  const [sectionSub, setSectionSub] = useState("A tight-knit team of specialists who have been with Thorpe Dellow through every chapter — from the first acquisition in 2006 to the NASDAQ Dubai listing and beyond.");

  const update = (id: number, k: string, v: string | boolean) => {
    setTeam((prev) => prev.map((m) => m.id === id ? { ...m, [k]: v } : m));
  };

  const addMember = () => {
    const newId = Math.max(...team.map((m) => m.id)) + 1;
    setTeam((prev) => [...prev, { id: newId, name: "New Team Member", role: "Role Title", bio: "Bio description here.", visible: true }]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>Team Members</h1>
        <p className="text-slate-500 text-sm mt-1">Manage the leadership team shown on the About page.</p>
      </div>

      <AdminCard title="Section Header">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
            <input value={sectionTitle} onChange={(e) => setSectionTitle(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle</label>
            <textarea value={sectionSub} onChange={(e) => setSectionSub(e.target.value)} rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
          </div>
        </div>
      </AdminCard>

      <AdminCard
        title={`Team Members (${team.filter((m) => m.visible).length} visible)`}
        actions={
          <button onClick={addMember} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#C9A84C]/10 text-[#C9A84C] text-xs font-semibold hover:bg-[#C9A84C]/20 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line leading-none" />
            Add Member
          </button>
        }
      >
        <div className="space-y-4">
          {team.map((m) => (
            <div key={m.id} className={`rounded-xl border p-4 transition-all ${m.visible ? "bg-slate-50 border-slate-100" : "bg-slate-50/50 border-slate-100 opacity-60"}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C9A84C]/15 text-[#C9A84C] text-xs font-bold">
                    {m.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#071A2E]">{m.name}</p>
                    <p className="text-xs text-slate-400">{m.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => update(m.id, "visible", !m.visible)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${m.visible ? "bg-green-50 text-green-600 hover:bg-green-100" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
                  >
                    <i className={`${m.visible ? "ri-eye-line" : "ri-eye-off-line"} leading-none`} />
                    {m.visible ? "Visible" : "Hidden"}
                  </button>
                  <button
                    onClick={() => setTeam((prev) => prev.filter((x) => x.id !== m.id))}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-sm leading-none" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Full Name</label>
                  <input value={m.name} onChange={(e) => update(m.id, "name", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Role / Title</label>
                  <input value={m.role} onChange={(e) => update(m.id, "role", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Bio</label>
                  <textarea value={m.bio} onChange={(e) => update(m.id, "bio", e.target.value)} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex justify-end">
        <button onClick={onSave} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#071A2E] text-white text-sm font-semibold hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-save-line leading-none" />
          Save Changes
        </button>
      </div>
    </div>
  );
}
